export const enUS = {
    common: {
        actions: 'Actions',
        add: 'Add',
        cancel: 'Cancel',
        close: 'Close',
        confirm: 'Confirm',
        delete: 'Delete',
        edit: 'Edit',
        save: 'Save',
        search: 'Search',
        loading: 'Loading...',
        noData: 'No data found',
        required: 'Required',
    },
    auth: {
        login: 'Login',
        logout: 'Logout',
        email: 'Email',
        password: 'Password',
    },
    components: {
        grid: {
            filters: 'Filters',
            activeFilters: 'Active filters',
            addFilter: 'Add filter',
            column: 'Column',
            operator: 'Operator',
            value: 'Value',
            clearAll: 'Clear all',
            close: 'Close',
            noData: 'No data found',
            actions: 'Actions',
            delete: 'Delete',
            showAll: 'Show all',
            hideAll: 'Hide all',
            columnsVisible: '{visible} of {total} columns visible',
            columns: 'Columns',
            density: 'Density',
            export: 'Export',
            manageColumns: 'Manage Columns',
        },
        dialog: {
            title: 'Dialog',
            selectIcon: 'Select Icon',
            close: 'Close',
            confirm: 'Confirm',
            cancel: 'Cancel',
        },
        iconPicker: {
            searchPlaceholder: 'Search icon...',
        }
    },
    validation: {
        requiredField: 'Field required',
        invalidEmail: 'Invalid email',
    },
    settings: {
        title: 'Settings',
        appearance: {
            title: 'Appearance',
            theme: 'Theme',
            light: 'Light',
            dark: 'Dark',
            system: 'System',
        },
        language: {
            title: 'Language',
            label: 'Language',
            portuguese: 'Portuguese',
            english: 'English',
        },
        about: {
            title: 'About',
            version: 'Version',
            developedBy: 'Developed by',
        },
    },
    // Mobile app shell only (request 0158 §7) — no business-module screens exist yet.
    mobile: {
        login: {
            subtitle: 'Sign in to your SysColect account.',
        },
        resetPassword: {
            title: 'Reset password',
            subtitle: "Enter your email and we'll send a link to reset your password.",
            backToLogin: 'Back to login',
            sent: "If that email exists, we've sent a link to reset your password.",
            sendLink: 'Send link',
            genericError: 'Could not send the link. Check the email and try again.',
        },
        authErrors: {
            invalidCredentials: 'Invalid credentials. Check your email and password.',
            networkError: 'No connection. Check your internet and try again.',
            loginFailed: 'Login failed. Please try again shortly.',
        },
        workspace: {
            emptyTitle: 'No modules enabled for the app',
            emptyDescription: "None of this organization's modules have a screen in the app. Ask an administrator to enable modules for mobile.",
        },
        group: {
            empty: 'No items in this group',
        },
        module: {
            // Intentionally identical in both languages (request 0158 §7): this is a catalog
            // diagnostic for the admin, not product copy — the exact text helps whoever fixes the
            // catalog find it, so it doesn't vary with the user's language.
            missingTitle: 'Módulo sem tela no aplicativo',
            missingDescription: 'O slug "{{slug}}" está marcado para a plataforma mobile, mas não tem uma tela nativa cadastrada. Isso é um problema de cadastro do módulo, não do aparelho.',
        },
        noOrganization: {
            title: 'No organization found',
            description: 'Your account is not associated with any active organization. Contact an administrator to get access.',
            signOutWithEmail: 'Sign out ({{email}})',
        },
        userSheet: {
            title: 'Account',
            organization: 'Organization',
            profile: 'Profile',
            noValue: '—',
        },
        sessionSelectors: {
            current: 'Current',
            noProfiles: 'No profile available in this organization.',
        },
        drawer: {
            workspace: 'Workspace',
            openMenu: 'Open menu',
        },
        privacyPolicy: 'Privacy Policy',
        userMenuButton: 'Open user menu',
        configGate: {
            loading: 'Loading configuration…',
            errorTitle: 'Could not load configuration.',
            retry: 'Try again',
        },
        session: {
            resolving: 'Loading your organization…',
        },
        status: {
            title: 'Status',
            systemApiTitle: 'System API',
            systemApiDescription: 'Required connection — active session and org/profile switches notified in real time.',
            monitorTitle: 'Monitor',
            monitorDescription: 'Optional connection, for point-in-time diagnostics only — off by default.',
            appTitle: 'App',
            version: 'Version',
            platform: 'Platform',
            sessionTitle: 'Session',
            sessionDescription: 'Recreated on every app open (known limitation). If support asks for the session id, this is it.',
            copy: 'Copy',
            connectMonitor: 'Connect monitor',
            markDisconnected: 'Mark as disconnected',
            notConfigured: 'not configured',
            origin: 'Source: {{source}}',
            connecting: 'Connecting',
            connected: 'Connected',
            closing: 'Closing',
            disconnected: 'Disconnected',
            error: 'Error',
        },
        settings: {
            aboutTitle: 'About',
            appearanceDescription: 'Interface theme (saved to your account).',
            languageDescription: 'Interface language (saved to your account).',
            // Endonyms — shown in their own language, not translated, same as any language switcher
            // (the app doesn't show "Inglês" in Portuguese, it shows "English").
            portugueseLabel: 'Português',
            englishLabel: 'English',
            scanTitle: 'Scanning',
            scanDescription: 'Physical-reader collector: turns off the on-screen keyboard.',
            scanWedgeLabel: 'Collector (physical reader)',
            scanManualLabel: 'Phone (typing or camera)',
            notificationsTitle: 'Notifications',
            notificationsDescription: 'Push on this device (saved to your account).',
            notificationsMaster: 'Receive notifications on this phone',
            notificationsPermissionGranted: 'Permission granted.',
            notificationsPermissionDenied: 'Permission denied — enable it in Android settings.',
            notificationsPermissionUndetermined: 'Permission not requested yet.',
            notificationsOpenSettings: 'Open Android settings',
            notificationsCategories: 'Categories',
            notificationsOrgLocked: 'Set by the organization',
            notificationsFooter: 'Notifications still appear in the app bell.',
        },
        notifications: {
            title: 'Notifications',
            empty: 'No notifications',
            filterUnread: 'Unread',
            filterAll: 'All',
            markAllRead: 'Mark all as read',
            loadMore: 'Load more',
            archive: 'Archive',
        },
        errorBoundary: {
            title: 'Something went wrong.',
            description: 'The app hit an unexpected error.',
            retry: 'Try again',
        },
        // Request 0159 — read service, shared by any field that accepts a barcode.
        scan: {
            openCamera: 'Open camera',
            close: 'Close',
            permissionDenied: 'Camera permission denied. You can still type the code.',
            openSettings: 'Open settings',
        },
        // Request 0159 — WMS collector (warehouse task queue + execution).
        warehouseOperator: {
            title: 'Run tasks',
            description: 'Driven queue — grab a task and follow the roteiro step by step.',
            refresh: 'Refresh queue',
            myTasksTitle: 'Continue',
            availableTasksTitle: 'Available',
            continueAction: 'Continue',
            claimAction: 'Grab',
            emptyTitle: 'No tasks in the queue',
            loadingQueue: 'Loading queue…',
            lineProgress: 'Line {{current}} of {{total}}',
            pause: 'Pause',
            resume: 'Resume',
            release: 'Release task',
            skipLine: 'Defer line',
            noLines: 'Task has no lines.',
            taskCompleteTitle: 'Task complete',
            backToQueue: 'Back to the queue',
            completionLines: '{{count}} lines',
            completionLinesOne: '1 line',
            deferredRemaining: '{{count}} deferred lines remain.',
            deferredRemainingOne: '1 deferred line remains.',
            resumeDeferred: 'Resume deferred',
            finishWithDeferred: 'Finish with deferred lines',
            invalidQuantity: 'Enter a number greater than or equal to zero.',
            allDone: 'Roteiro complete.',
            retry: 'Try again',
            unknownStepKind: 'Step "{{kind}}" has no screen in this app — tell support.',
            scanHuLabel: 'Volume code',
            scanAddressLabel: 'Address code',
            confirmAction: 'Confirm',
            expectedQuantity: 'expected {{value}}',
            operatorNoteTitle: 'Note to operator',
            fieldProduct: 'Product',
            fieldVariant: 'Variant',
            fieldLot: 'Lot',
            fieldVolume: 'Volume',
            fieldLocation: 'Location',
            fieldAddress: 'Address',
            listProgress: '{{informed}} of {{total}} entered',
            quantityPromptTitle: 'Enter quantity',
            quantityPromptNext: 'Next',
            finishCheck: 'Finish check',
            finishCount: 'Finish count',
            missingQuantities: 'Enter the quantity for every line before finishing.',
            partialFailure: '{{count}} lines were not saved. Fix and finish again.',
            retryFailed: 'Try again ({{count}})',
            reloadTask: 'Reload task',
            // Request 0340 — enter volumes on the collector
            volumes: {
                openButton: 'Volumes',
                noneEntered: 'No volumes entered',
                title: 'Volumes — {{product}}',
                expected: 'Expected: {{value}}',
                expectedBoth: 'Expected: {{base}} ({{typed}})',
                informIn: 'Enter in',
                standard: 'Standard',
                extras: 'Different from standard',
                volumesField: 'Volumes',
                weightEachLabel: 'Weight each ({{unit}})',
                totalLabel: 'Total ({{unit}})',
                damagedLabel: 'Damaged ({{unit}})',
                addExtra: 'Add another weight',
                removeExtra: 'Remove',
                summaryVolumes: 'Volumes',
                summaryChecked: 'Checked',
                summaryStock: 'In stock',
                diffEqual: 'Matches expected.',
                diffEqualShort: 'Matches expected',
                diffLess: '{{value}} less than expected. Held as divergence for accept.',
                diffLessShort: '{{value}} less than expected',
                diffMore: '{{value}} more than expected. Volumes stay held until accept.',
                diffMoreShort: '{{value}} more than expected',
                applyEmpty: 'Enter at least one volume.',
                cancel: 'Cancel',
                apply: 'Apply',
                quantityPromptTitle: 'Enter value',
                volumeCount: '{{count}} volumes',
                volumeCountOne: '1 volume',
                materializing: 'Creating volumes…',
                materializeError: 'Could not create the volumes.',
                generated: '{{count}} volumes created',
                generatedOne: '1 volume created',
                held: '{{count}} items await divergence accept',
                heldOne: '1 item awaits divergence accept',
                printViaReceiving: 'Labels print from receiving or the station.',
            },
        },
        // Request 0205 — production apontamento (scan + quantity + execution, Phase B1;
        // machine stop mode, Phase B2).
        productionReport: {
            title: 'Report production',
            modeEntry: 'Entry',
            modeStop: 'Stop',
            scanLabelTicketOnly: 'Ticket',
            scanLabelTicketOrOrder: 'Ticket or order',
            scanPlaceholder: 'Scan or type the code',
            newSearch: 'New search',
            change: 'Change',
            loading: 'Loading…',
            genericError: 'Could not complete the action.',
            invalidQuantity: 'Enter a valid quantity.',
            quantityLabel: 'Quantity',
            confirmButton: 'Confirm',
            pointedOfPlanned: 'Reported so far: {{pointed}} of {{planned}}',
            statusLabel: 'Status: {{status}}',
            orderStatus: {
                draft: 'Draft',
                released: 'Released',
                'in-progress': 'In progress',
                completed: 'Completed',
                cancelled: 'Cancelled',
            },
            ticketStatus: {
                open: 'Open',
                'in-progress': 'In progress',
                done: 'Done',
                cancelled: 'Cancelled',
            },
            executionContextTitle: 'Execution context',
            executionSummary: 'Step {{step}} · Machine {{machine}} · {{operator}}',
            stepLabel: 'Step',
            machineLabel: 'Machine',
            operatorLabel: 'Operator',
            noStep: 'None',
            noMachine: 'Optional',
            noOperator: "Who's typing",
            stepWithProcess: 'Step {{order}} — {{process}}',
            stepOnly: 'Step {{order}}',
            selectStepToStart: 'Select the step to start.',
            noStepsWarning: 'This organization requires a step to report production.',
            requireRunWarning: 'This organization requires a started execution to report production.',
            startButton: 'Start',
            runningLabel: 'Running',
            pausedLabel: 'Stopped',
            stopButton: 'Stop',
            resumeButton: 'Resume',
            finishButton: 'Finish',
            concurrentWarning: 'There is already an open execution since {{startedAt}}. This does not prevent starting another one.',
            resumeOther: 'Resume',
            startAnother: 'Start another',
            stopSheetTitle: 'Stop',
            stopReasonLabel: 'Reason',
            stopReasonOptionalLabel: 'Reason (optional)',
            stopReasonNone: 'No reason',
            notesLabel: 'Notes',
            confirmStopButton: 'Stop',
            sessionTitle: 'This session ({{count}})',
            sessionEmpty: 'No entries in this session yet.',
            sessionEntryLine: '{{quantity}} on {{label}}',
            toastSuccess: '{{quantity}} un on {{label}}',
            toastPending: 'Entry saved. Stock pending.',
            toastStartSuccess: 'Execution started.',
            toastFinishSuccess: 'Execution finished.',
            toastStopSuccess: 'Stop recorded.',
            toastResumeSuccess: 'Production resumed.',
            machineStop: {
                scanLabel: 'Machine',
                scanPlaceholder: 'Scan or type the machine code',
                codeLabel: 'Code {{code}}',
                runWarning: 'This machine is running order «{{order}}». Stopping it will pause that execution.',
                stopButton: 'Stop machine',
                stoppedLabel: 'Stopped',
                resumeButton: 'Resume machine',
                toastStopped: 'Machine stopped.',
                toastPaused: 'Execution paused.',
                toastResumed: 'Machine resumed.',
            },
        },
        // Request 0334 — stock balance lookup (read-only).
        stockBalance: {
            scanLabel: 'Product',
            scanPlaceholder: 'Scan or type the code',
            productNotFound: 'Product not found',
            genericError: 'Could not look up the balance.',
            loading: 'Loading…',
            totalBalance: 'Total balance',
            available: 'Available',
            reservedAvailable: 'Reserved {{reserved}} · Available {{available}}',
            availableOnly: 'Available {{available}}',
            lotLine: 'Lot {{lot}}',
            noLots: 'No lots at this location.',
            emptyBalances: 'No balance for this product',
            owner: 'Owner: {{name}}',
            noVariant: 'No variant',
        },
        // Request 0334 — volume lookup (read-only).
        handlingUnit: {
            scanLabel: 'Volume',
            scanPlaceholder: 'Scan or type the label code',
            volumeNotFound: 'Volume not found',
            loading: 'Loading…',
            genericError: 'Could not look up the volume.',
            quantity: 'Quantity',
            address: 'Address',
            noAddress: 'No address',
            location: 'Location',
            entryDate: 'Received',
            variant: 'Variant {{label}}',
            lot: 'Lot {{label}}',
            history: 'History',
            historyEmpty: 'No movements recorded.',
            fromTo: '{{from}} → {{to}}',
            quantityMeta: 'Qty {{quantity}}',
            userMeta: '{{name}}',
        },
    },
};
//# sourceMappingURL=en-US.js.map